import { Component } from "@/types/types"
import { cx } from "@/utils/cx"
import { ChangeEvent, HTMLInputAutoCompleteAttribute, useRef } from "react"
import { Stack } from "@/ui/atoms/layout/Stack"
import { tx } from "@/utils/tx"

export const COMPONENT_NAME = "textField"

export const presets = {}

export interface Props extends Component {
  name: string
  label: string | null
  placeholder?: string
  defaultValue?: string
  type?: "text" | "email"
  helperText?: string
  error?: string
  disabled?: boolean
  autoComplete?: HTMLInputAutoCompleteAttribute
  maxLength?: number
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

export function TextField({
  name,
  label,
  placeholder = "",
  defaultValue = "",
  type = "text",
  helperText = "",
  error = "",
  disabled,
  autoComplete,
  maxLength,
  onChange,
  className,
  testId = COMPONENT_NAME,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Stack className={cx(`gap-2 w-full`, className)} testId={testId}>
      <div className="flex justify-between">
        {!!label && (
          <label
            htmlFor={name}
            className={cx(
              "leading-none text-text-secondary font-semibold",
              disabled && "text-text-secondary-disabled",
            )}
            data-test={tx(testId, "label")}
          >
            {label}
          </label>
        )}

        {helperText && (
          <div
            className={cx("text-xs text-text-subtle", disabled && "text-text-subtle-disabled")}
            data-test={tx(testId, "helpText")}
          >
            Helper text
          </div>
        )}
      </div>
      <input
        name={name}
        id={name}
        type="text"
        inputMode={type}
        defaultValue={defaultValue}
        placeholder={placeholder}
        autoComplete={autoComplete}
        ref={inputRef}
        disabled={disabled}
        onChange={onChange}
        maxLength={maxLength}
        className={cx(
          `block bg-surface text-text border border-border-form rounded-md p-2 leading-none`,
          `h-[38px] focus-ring`,
          "placeholder:text-text-subtle",
          "disabled:bg-surface-disabled",
        )}
        data-test={tx(testId, "input")}
      />
      {!!error && (
        <div className="text-xs text-text-critical leading-tight" data-test={tx(testId, "error")}>
          {error}
        </div>
      )}
    </Stack>
  )
}
